import { useCallback, useSyncExternalStore } from 'react';
import { sitePaletteTokens, type SitePalettes } from '@anidex/shared/premium';

export type ThemeChoice = 'auto' | 'light' | 'dark';
export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'anidex-theme';
const LIGHT_QUERY = '(prefers-color-scheme: light)';
const THEME_COLORS: Record<Theme, string> = { light: '#EDF1F9', dark: '#111A2E' };
const listeners = new Set<() => void>();

let fallbackChoice: ThemeChoice = 'auto';
let personalPalettes: SitePalettes | null = null;
const personalTokens = new Set<string>();

function readChoice(): ThemeChoice {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === 'light' || value === 'dark' ? value : 'auto';
  } catch {
    return fallbackChoice;
  }
}

function resolve(choice: ThemeChoice): Theme {
  if (choice !== 'auto') return choice;
  return window.matchMedia(LIGHT_QUERY).matches ? 'light' : 'dark';
}

function apply() {
  const theme = resolve(readChoice());
  document.documentElement.dataset.theme = theme;
  const root = document.documentElement;
  for (const token of personalTokens) root.style.removeProperty(token);
  personalTokens.clear();
  const palette = personalPalettes?.[theme];
  if (palette) {
    for (const [token, value] of Object.entries(sitePaletteTokens(palette))) {
      root.style.setProperty(token, value);
      personalTokens.add(token);
    }
    root.dataset.personalPalette = 'true';
  } else {
    delete root.dataset.personalPalette;
  }
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', palette?.background ?? THEME_COLORS[theme]);
  for (const listener of listeners) listener();
}

export function setPersonalPalettes(palettes: SitePalettes | null): void {
  personalPalettes = palettes;
  apply();
}


export function watchTheme() {
  window.matchMedia(LIGHT_QUERY).addEventListener('change', apply);
  window.addEventListener('storage', (event) => {
    if (event.key === STORAGE_KEY) apply();
  });
  apply();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useTheme() {
  const choice = useSyncExternalStore(subscribe, readChoice, (): ThemeChoice => 'auto');
  const theme = useSyncExternalStore(subscribe, () => resolve(readChoice()), (): Theme => 'dark');

  const setChoice = useCallback((next: ThemeChoice) => {
    try {
      if (next === 'auto') localStorage.removeItem(STORAGE_KEY);
      else localStorage.setItem(STORAGE_KEY, next);
    } catch {
      fallbackChoice = next;
    }
    apply();
  }, []);

  return { choice, theme, setChoice };
}
